import { useLazyQuery } from '@apollo/client';
import useEnableFastifyApi from './useEnableFastifyApi';
import { RETRIEVE_BOOKING } from '../queries';
import restRequest from './restClient';
import useAsyncCall from './useAsyncCall';

function toGraphqlShape(bffResponse) {
  return { retrieveBooking: bffResponse };
}

// The BFF forwards Manage's outcome as the error code; the legacy GraphQL stack surfaced ESL's
// own codes instead (via the amend subgraph), and FormReservation/Manage/ResultsDeparture branch
// their error banners on those. This maps outcome -> legacy code so findErrorCode keeps working.
const MANAGE_OUTCOME_TO_LEGACY_CODE = {
  BOOKING_LOCKED: 'CSE0098',
  BOOKING_CANCELLED: 'BOOKING_STATUS_CANCELLED',
  SAILING_NOT_FOUND: 'INCORRECT_SAILING_BOOKING_DATE',
};

const ABSENT_BOOKING_CODES = [
  'MANAGE_API_ERROR',
  'BOOKING_NOT_FOUND',
  'ROOM_INFORMATION_NOT_FOUND',
];

const RETRIEVE_TIMEOUT_MS = 75_000;

function toLegacyErrorCodes(error) {
  const graphQLErrors = (error?.graphQLErrors ?? []).map((gqlError) => {
    let legacyCode = MANAGE_OUTCOME_TO_LEGACY_CODE[gqlError.extensions?.code];
    // Akamai replaces any 404 body with the brand's HTML error page, so the code can be missing
    // entirely. The BFF answers booking STATE with a 400 or a 423, so a 404 here is absence.
    if (!legacyCode && gqlError.extensions?.statusCode === 404) {
      const code = gqlError.extensions?.code;
      if (!code || ABSENT_BOOKING_CODES.includes(code)) legacyCode = 'CSE0293';
    }
    return legacyCode
      ? { ...gqlError, extensions: { ...gqlError.extensions, code: legacyCode } }
      : gqlError;
  });
  // Mutate and rethrow the ORIGINAL error: spreading an Error into a plain object drops its
  // non-enumerable message/stack and its prototype, so String(error) became '[object Object]' —
  // exactly the branch-by-toString contract restClient's Error instances exist to preserve.
  if (error instanceof Error) {
    // eslint-disable-next-line no-param-reassign
    error.graphQLErrors = graphQLErrors;
    throw error;
  }
  // eslint-disable-next-line no-throw-literal
  throw { ...error, graphQLErrors };
}

export default function useBookingRetrieve(options) {
  const enableFastifyApi = useEnableFastifyApi();

  const graphqlResult = useLazyQuery(RETRIEVE_BOOKING, options);
  // The {variables} unwrapping lives INSIDE the asyncFn (not in a per-render wrapper around
  // restExecute) so the returned execute keeps useAsyncCall's stable identity — components put
  // these execute functions in useEffect dependency arrays (Apollo's are stable), and a fresh
  // closure per render caused an infinite re-fetch loop.
  const [restExecute, restState] = useAsyncCall(
    ({ variables: { bookingId, lastName, shipCode, sailDate } = {} } = {}) => {
      const deadline = new AbortController();
      const timer = setTimeout(() => deadline.abort(), RETRIEVE_TIMEOUT_MS);
      // Only when the caller supplied a complete set: this same hook also serves the token-only
      // flow (the manage redirect arrives with ?token= and no identifiers), and suppressing the
      // header there would leave the request with nothing to identify a booking by.
      //
      // Where identifiers ARE present the guest typed them, so the call carries its own identity
      // and must not also send the ambient `x-booking-token` from session storage — with both
      // present the BFF preferred the token and answered with whatever booking the tab had opened
      // before. The stored token is left alone: FormReservation swaps it via saveBookingToken on
      // a successful retrieve, and clearing it up front would discard a still-valid one whenever
      // the lookup fails.
      const hasIdentifiers = Boolean(bookingId && lastName && shipCode && sailDate);
      return restRequest({
        path: '/air-api/v1/bookings/retrieve',
        params: { bookingId, lastName, shipCode, sailDate },
        signal: deadline.signal,
        withoutBookingToken: hasIdentifiers,
      })
        .then(toGraphqlShape)
        .catch(toLegacyErrorCodes)
        .finally(() => clearTimeout(timer));
    },
    options,
  );

  if (enableFastifyApi) {
    return [restExecute, restState];
  }
  return graphqlResult;
}
